import type { QueryClient } from '@tanstack/react-query'

import type { TribeClient } from '@tribeplatform/gql-client/client'
import type { Post } from '@tribeplatform/gql-client/types'

import {
  followStateUpdater,
  getFollowStateVersion,
} from '../../utils/post-follow.util.js'
import { updatePostCaches } from '../cache/useCachedPost.js'

// Time for the notification consumer to persist the auto-follow.
export const FOLLOW_REFRESH_DELAY_MS = 3000
// Reads before "not followed" is accepted as the server's answer.
export const FOLLOW_REFRESH_ATTEMPTS = 3

/**
 * Re-reads a post's follow state after a reply until the consumer has
 * followed it. A response is dropped when the follow state changed locally
 * in the meantime; "not followed" is only applied on the last attempt.
 * `maxAttempts` bounds the reads: pass 1 when no follow is expected, so a
 * correct "not followed" answer is accepted at once.
 */
export const scheduleFollowRefresh = (
  postId: string,
  client: TribeClient,
  queryClient: QueryClient,
  maxAttempts = FOLLOW_REFRESH_ATTEMPTS,
  attempt = 1,
): void => {
  const version = getFollowStateVersion(postId)

  setTimeout(async () => {
    let fresh: Post
    try {
      fresh = await client.posts.get(
        { id: postId },
        { authMemberProps: 'basic' },
      )
    } catch {
      // Keep the optimistic state.
      return
    }

    if (getFollowStateVersion(postId) !== version) {
      return
    }

    const subscribed = !!fresh?.authMemberProps?.subscribed
    if (!subscribed && attempt < maxAttempts) {
      scheduleFollowRefresh(
        postId,
        client,
        queryClient,
        maxAttempts,
        attempt + 1,
      )
      return
    }

    updatePostCaches(
      postId,
      followStateUpdater(subscribed, fresh?.authMemberProps?.subscriptionLevel),
      queryClient,
    )
  }, FOLLOW_REFRESH_DELAY_MS)
}
